import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { 
  Users, 
  AlertTriangle, 
  Clock, 
  Shield, 
  Waves, 
  Heart,
  Sailboat,
  RefreshCw,
  ChevronRight,
  LifeBuoy,
  BookOpen,
  Syringe,
  HeartPulse
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const categoryIcons = {
  cpr_aed: Shield,
  cpr_aed_rte_combo: BookOpen,
  lifeguard: LifeBuoy,
  health_nursing: Heart,
  boating: Sailboat,
  ceu: BookOpen,
  epipen: Syringe,
  bls: HeartPulse,
  pal: Waves,
};

const categoryLabels = {
  cpr_aed: "CPR/AED",
  cpr_aed_rte_combo: "AED/CPR/First Aid Combo",
  cpr_aed_instructor: "CPR/AED Instructor",
  lifeguard: "Lifeguard",
  wsi: "Water Safety Instructor (WSI)",
  health_nursing: "Health/Nursing",
  boating: "Boating",
  ceu: "CEU/CPR",
  epipen: "Epi Pen/Anaphylaxis",
  bls: "Basic Life Support",
  pal: "Pool Activity Leader",
};

export default function DashboardPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [token]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/campminder/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Overview of staff certifications and alerts
          </p>
        </div>
        <Button
          onClick={handleSync}
          disabled={syncing}
          className="bg-brand-navy hover:bg-brand-navy/90 btn-lift"
          data-testid="sync-campminder-button"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing..." : "Sync CampMinder"}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6 card-animate stagger-1" data-testid="stats-total-staff">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-brand-navy/10 flex items-center justify-center">
              <Users className="h-6 w-6 text-brand-navy" strokeWidth={2} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Total Staff
              </p>
              <p className="text-3xl font-heading font-bold text-slate-900">
                {stats?.total_staff || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 card-animate stagger-2" data-testid="stats-expired-certs">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-red-600" strokeWidth={2} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Expired Certs
              </p>
              <p className="text-3xl font-heading font-bold text-red-600">
                {stats?.expired_certs || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 card-animate stagger-3" data-testid="stats-expiring-soon">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="h-6 w-6 text-amber-600" strokeWidth={2} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Expiring Soon
              </p>
              <p className="text-3xl font-heading font-bold text-amber-600">
                {stats?.expiring_soon || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts by Category */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.entries(stats?.alerts_by_category || {}).map(([category, alerts], index) => {
          const Icon = categoryIcons[category] || Shield;
          const hasAlerts = alerts.length > 0;
          const isExpanded = expandedCategories[category];
          const visibleAlerts = isExpanded ? alerts : alerts.slice(0, 5);
          
          return (
            <div 
              key={category} 
              className={`bg-white border border-slate-200 rounded-lg overflow-hidden card-animate`}
              style={{ animationDelay: `${(index + 4) * 50}ms` }}
              data-testid={`alerts-category-${category}`}
            >
              {/* Category Header */}
              <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
                <div className="h-8 w-8 rounded-md bg-brand-navy/10 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-brand-navy" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-slate-900">
                    {categoryLabels[category]}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {alerts.length} alert{alerts.length !== 1 ? "s" : ""}
                  </p>
                </div>
              </div>

              {/* Alert List */}
              <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                {hasAlerts ? (
                  visibleAlerts.map((alert, i) => (
                    <div
                      key={`${alert.staff_id}-${alert.cert_type}-${i}`}
                      className="px-6 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/staff/${alert.staff_id}`)}
                      data-testid={`alert-item-${alert.staff_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            alert.status === "expired"
                              ? "bg-red-100 text-red-800 border border-red-300"
                              : "bg-amber-100 text-amber-800 border border-amber-300"
                          }`}
                        >
                          {alert.status === "expired" ? "Expired" : "Expiring"}
                        </span>
                        <span className="text-sm font-medium text-slate-900">
                          {alert.staff_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <span>
                          {alert.expiration_date
                            ? new Date(alert.expiration_date).toLocaleDateString()
                            : "N/A"}
                        </span>
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-6 py-8 text-center">
                    <div className="h-12 w-12 rounded-full bg-emerald-50 mx-auto mb-3 flex items-center justify-center">
                      <Shield className="h-6 w-6 text-emerald-600" strokeWidth={2} />
                    </div>
                    <p className="text-sm text-slate-500">
                      All certifications are current
                    </p>
                  </div>
                )}
              </div>
              
              {alerts.length > 5 && (
                <div className="px-6 py-3 border-t border-slate-100 text-center">
                  <button
                    onClick={() => setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }))}
                    className="text-sm text-brand-navy hover:underline font-medium"
                    data-testid={`toggle-alerts-${category}`}
                  >
                    {isExpanded ? "Show less" : `View all ${alerts.length} alerts`}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
